<template>
  <div class="space-y-4">
    <div>
      <p class="text-sm font-semibold text-black">Photos par couleur</p>
      <p class="text-xs text-gray-500 mt-1">
        Chaque photo doit être associée à une couleur. Les images Shopify sans
        couleur de variant sont ignorées.
      </p>
    </div>

    <div
      v-if="loading"
      class="flex h-28 items-center justify-center text-gray-500"
    >
      <i class="fas fa-spinner fa-spin mr-2"></i>
      Chargement des photos…
    </div>

    <template v-else>
      <p
        v-if="!selectedColors?.length"
        class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
      >
        Sélectionnez au moins une couleur pour pouvoir associer les photos.
      </p>
      <p
        v-else-if="!localPhotos.length"
        class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
      >
        Aucune photo associée à une couleur. Ajoutez au moins une image par
        couleur — obligatoire pour mettre le produit en ligne sur eFashion.
      </p>

      <section
        v-for="section in photoSections"
        :key="section.key"
        class="border border-gray-200 rounded-lg p-3 space-y-3 bg-white"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <span
              v-if="section.hexColor"
              class="w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0"
              :style="{ backgroundColor: section.hexColor }"
            ></span>
            <h3 class="text-sm font-medium text-gray-900 truncate">
              {{ section.label }}
              <span
                v-if="linkedCaptionFor(section.colorId)"
                class="text-gray-500 font-normal"
              >
                · {{ linkedCaptionFor(section.colorId) }}
              </span>
              <span class="text-gray-500 font-normal">
                ({{ section.photos.length }})
              </span>
            </h3>
          </div>
        </div>

        <div
          v-if="section.photos.length"
          class="flex gap-3 overflow-x-auto pb-1"
        >
          <div
            v-for="(entry, indexInSection) in section.photos"
            :key="entry.photo.key"
            class="relative group flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
            :class="[
              draggingKey === entry.photo.key ? 'opacity-50 scale-95' : '',
              dragOverKey === entry.photo.key
                ? 'ring-2 ring-blue-400 ring-offset-1'
                : '',
              isCover(entry.photo) ? 'border-2 border-blue-500' : '',
            ]"
            :draggable="!persisting"
            @dragstart="handleDragStart($event, entry.photo.key)"
            @dragend="handleDragEnd"
            @dragover="handleDragOver($event, entry.photo.key)"
            @drop="handleDrop($event, section.key, entry.photo.key)"
          >
            <img
              :src="entry.photo.previewUrl"
              :alt="`Photo ${indexInSection + 1}`"
              class="w-full h-full object-contain pointer-events-none transition-transform duration-200"
              :style="{ transform: `rotate(${entry.photo.rotation}deg)` }"
            />
            <div
              v-if="isCover(entry.photo)"
              class="absolute top-0 left-0 right-0 bg-blue-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-b text-center leading-tight pointer-events-none"
            >
              Couverture
            </div>
            <div
              class="absolute inset-0 bg-black/50 transition-opacity grid grid-cols-2 gap-1 items-center justify-center content-center p-1 opacity-100 pointer-events-auto md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto"
            >
              <button
                v-if="!isCover(entry.photo)"
                type="button"
                class="p-2 bg-white/90 text-yellow-500 rounded-full hover:bg-white disabled:opacity-60"
                title="Définir comme photo de couverture"
                :disabled="persisting"
                @click.stop="setCover(entry.photo.key)"
              >
                <i class="fas fa-star text-sm"></i>
              </button>
              <button
                type="button"
                class="p-2 bg-white/90 text-blue-600 rounded-full hover:bg-white disabled:opacity-60"
                title="Pivoter la photo"
                :disabled="persisting"
                @click.stop="rotatePhoto(entry.photo.key)"
              >
                <i class="fas fa-redo-alt text-sm"></i>
              </button>
              <button
                v-if="localPhotos.length > 1"
                type="button"
                class="p-2 bg-white/90 text-red-600 rounded-full hover:bg-white disabled:opacity-60"
                title="Supprimer la photo"
                :disabled="persisting"
                @click.stop="requestDelete(entry.photo.key)"
              >
                <i class="fas fa-trash-alt text-sm"></i>
              </button>
            </div>
          </div>
        </div>
        <p v-else class="text-xs text-gray-500">Aucune photo pour cette couleur.</p>

        <div
          :class="[
            'border-2 border-dashed rounded-lg p-3 text-center transition-all duration-200',
            uploadSectionKey === section.key && isDragOverUpload
              ? 'border-blue-500 bg-blue-50 shadow-md cursor-grabbing'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer bg-white',
            persisting || localPhotos.length >= maxPhotos
              ? 'opacity-60 pointer-events-none'
              : '',
          ]"
          @click="triggerFileDialog(section.key)"
          @dragenter.prevent="
            isDragOverUpload = true;
            uploadSectionKey = section.key;
          "
          @dragover.prevent="
            isDragOverUpload = true;
            uploadSectionKey = section.key;
          "
          @dragleave.prevent="
            isDragOverUpload = false;
            uploadSectionKey = null;
          "
          @drop.prevent="handleUploadDrop($event, section.key)"
        >
          <div
            class="flex flex-col items-center gap-2 justify-center min-h-[52px]"
          >
            <i
              class="fas fa-cloud-upload-alt text-xl"
              :class="
                uploadSectionKey === section.key && isDragOverUpload
                  ? 'text-blue-500'
                  : 'text-black'
              "
            ></i>
            <span
              v-if="uploading && uploadSectionKey === section.key"
              class="flex items-center gap-2 text-xs text-blue-600 font-medium"
            >
              <span
                class="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
              ></span>
              Téléversement…
            </span>
            <span v-else class="text-xs text-gray-600">
              Ajouter des photos — {{ section.label }}
            </span>
          </div>
        </div>
      </section>

      <p v-if="localPhotos.length || selectedColors?.length" class="text-xs text-gray-500">
        Chaque couleur sélectionnée doit avoir au moins une photo. La couverture
        est la première photo de la liste globale.
      </p>
    </template>

    <input
      ref="fileInputRef"
      type="file"
      class="hidden"
      accept="image/jpeg,image/png,image/webp,image/gif"
      multiple
      @change="handleFileInput"
    />

    <ConfirmModal
      :show="showDeleteModal"
      title="Supprimer la photo ?"
      message="Cette photo sera retirée de la sélection Shopify."
      confirm-label="Supprimer"
      cancel-label="Annuler"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import ConfirmModal from "@/components/common/ConfirmModal.vue";
import {
  getShopifyCustomImageUrl,
  saveShopifyProductImages,
  uploadShopifyCustomImage,
} from "@/api/shopify";
import type { PrestashopImageOverride } from "@/types/prestashop";
import type { ShopifyMediaItem } from "@/types/shopify";

type LocalPhoto = {
  key: string;
  type: "shopify" | "custom";
  shopifyImageId?: string;
  shopifyUrl?: string;
  customFile?: string;
  previewUrl: string;
  rotation: number;
  colorId: number | null;
  colorName: string | null;
};

type ColorOption = {
  id: number;
  nom: string;
  hexColor?: string | null;
};

const props = defineProps<{
  productGid: string;
  sourceImages: ShopifyMediaItem[];
  savedImages?: PrestashopImageOverride[];
  selectedColors?: ColorOption[];
  colorCollisions?: Array<{
    colorId: number;
    officialLabel: string;
    names: string[];
  }>;
  colorSourceChoices?: Array<{ sourceName: string; colorId: number }>;
  linkedSourceLabelsByColorId?: Record<number, string[]>;
}>();

const emit = defineEmits<{
  (event: "update:images", value: PrestashopImageOverride[]): void;
  (event: "saved", value: PrestashopImageOverride[]): void;
  (event: "error", message: string): void;
}>();

function normalizeCollisionLabel(value: string | null | undefined) {
  return value
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[^a-z0-9]+/g, " ")
    .trim() ?? "";
}

function collidingNames() {
  return (props.colorCollisions ?? []).flatMap((group) => group.names);
}

function isCollidingSourceName(name: string | null | undefined) {
  const key = normalizeCollisionLabel(name);
  if (!key) return false;
  return collidingNames().some(
    (label) => normalizeCollisionLabel(label) === key,
  );
}

function choiceForSourceName(name: string | null | undefined) {
  const key = normalizeCollisionLabel(name);
  if (!key) return null;
  return (
    (props.colorSourceChoices ?? []).find(
      (choice) => normalizeCollisionLabel(choice.sourceName) === key,
    ) ?? null
  );
}

function isAbandonedMappedColor(colorId: number) {
  const labels = props.linkedSourceLabelsByColorId?.[colorId] ?? [];
  if (labels.length < 2) return false;
  return labels.every((label) => {
    const choice = choiceForSourceName(label);
    return Boolean(choice && choice.colorId !== colorId);
  });
}

function chosenColorIds() {
  return new Set(
    (props.colorSourceChoices ?? []).map((choice) => choice.colorId),
  );
}

function officialNameFor(colorId: number) {
  return (
    (props.selectedColors ?? []).find((color) => color.id === colorId)?.nom ||
    ""
  );
}

function isUnresolvedCollision(name: string | null | undefined) {
  return isCollidingSourceName(name) && !choiceForSourceName(name);
}

function placeSourceColor(sourceName: string | null) {
  const choice = choiceForSourceName(sourceName);
  if (choice) {
    return {
      colorId: choice.colorId,
      colorName: officialNameFor(choice.colorId) || sourceName,
    };
  }
  if (isCollidingSourceName(sourceName)) {
    return { colorId: null, colorName: sourceName };
  }
  return null;
}

function collisionColorIds() {
  return new Set((props.colorCollisions ?? []).map((group) => group.colorId));
}

function linkedCaptionFor(colorId: number | null | undefined) {
  const id = Number(colorId);
  if (!Number.isInteger(id) || id <= 0) return "";
  const sheetLabels = (props.colorSourceChoices ?? [])
    .filter((choice) => choice.colorId === id)
    .map((choice) => choice.sourceName);
  if (sheetLabels.length) return `liée : ${sheetLabels.join(", ")}`;
  if (collisionColorIds().has(id)) return "";
  const labels = props.linkedSourceLabelsByColorId?.[id] ?? [];
  if (!labels.length) return "";
  return `liée : ${labels.join(", ")}`;
}

const maxPhotos = 20;
const allowedTypes = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const loading = ref(false);
const uploading = ref(false);
const persisting = ref(false);
const localPhotos = ref<LocalPhoto[]>([]);
const isDragOverUpload = ref(false);
const uploadSectionKey = ref<string | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const pendingUploadSectionKey = ref<string | null>(null);
const draggingKey = ref<string | null>(null);
const dragOverKey = ref<string | null>(null);
const showDeleteModal = ref(false);
const deleteKey = ref<string | null>(null);
const lastPersistedOverrides = ref<PrestashopImageOverride[]>([]);
let loadToken = 0;

function sectionKeyFor(colorId: number | null, colorName: string | null) {
  if (isUnresolvedCollision(colorName)) {
    return `color-name:${normalizeCollisionLabel(colorName)}`;
  }
  if (colorId && !collisionColorIds().has(colorId)) return `color-id:${colorId}`;
  if (colorName?.trim()) {
    return `color-name:${normalizeCollisionLabel(colorName)}`;
  }
  return null;
}

function hasColorAssociation(
  colorId: number | null | undefined,
  colorName: string | null | undefined
) {
  return Boolean(colorId) || Boolean(colorName?.trim());
}

function normalizeColorLabel(value: string | null | undefined) {
  return value?.trim().toLocaleLowerCase("fr") ?? "";
}

function matchSelectedColor(
  colorId: number | null,
  colorName: string | null
): ColorOption | null {
  const colors = props.selectedColors ?? [];
  if (colorId) {
    const byId = colors.find((color) => color.id === colorId);
    if (byId) return byId;
  }
  const needle = normalizeColorLabel(colorName);
  if (!needle) return null;
  return (
    colors.find(
      (color) => normalizeColorLabel(color.nom) === needle
    ) ?? null
  );
}

const photoSections = computed(() => {
  const sections = new Map<
    string,
    {
      key: string;
      label: string;
      hexColor: string | null;
      colorId: number | null;
      colorName: string | null;
      photos: Array<{ photo: LocalPhoto; globalIndex: number }>;
    }
  >();

  const ensureSection = (
    colorId: number | null,
    colorName: string | null
  ) => {
    if (!hasColorAssociation(colorId, colorName)) return null;
    const key = sectionKeyFor(colorId, colorName);
    if (!key) return null;
    if (sections.has(key)) return sections.get(key)!;
    const colliding = isUnresolvedCollision(colorName);
    const matched = colliding ? null : matchSelectedColor(colorId, colorName);
    const section = {
      key,
      label: colliding
        ? colorName?.trim() || "Couleur"
        : matched?.nom || colorName?.trim() || "Couleur",
      hexColor: colliding ? null : matched?.hexColor ?? null,
      colorId: colliding ? null : matched?.id ?? colorId,
      colorName: colorName?.trim() || matched?.nom || null,
      photos: [] as Array<{ photo: LocalPhoto; globalIndex: number }>,
    };
    sections.set(key, section);
    return section;
  };

  for (const color of props.selectedColors ?? []) {
    if (isAbandonedMappedColor(color.id)) continue;
    if (collisionColorIds().has(color.id) && !chosenColorIds().has(color.id)) {
      continue;
    }
    ensureSection(color.id, color.nom);
  }
  for (const name of collidingNames()) {
    if (choiceForSourceName(name)) continue;
    ensureSection(null, name);
  }

  localPhotos.value.forEach((photo, globalIndex) => {
    if (!hasColorAssociation(photo.colorId, photo.colorName)) return;
    const section = ensureSection(photo.colorId, photo.colorName);
    section?.photos.push({ photo, globalIndex });
  });

  const ordered: Array<{
    key: string;
    label: string;
    hexColor: string | null;
    colorId: number | null;
    colorName: string | null;
    photos: Array<{ photo: LocalPhoto; globalIndex: number }>;
  }> = [];
  for (const name of collidingNames()) {
    if (choiceForSourceName(name)) continue;
    const key = sectionKeyFor(null, name);
    if (!key) continue;
    const section = sections.get(key);
    if (section) ordered.push(section);
    sections.delete(key);
  }
  for (const color of props.selectedColors ?? []) {
    if (isAbandonedMappedColor(color.id)) continue;
    if (collisionColorIds().has(color.id) && !chosenColorIds().has(color.id)) {
      continue;
    }
    const key = sectionKeyFor(color.id, color.nom);
    if (!key) continue;
    const section = sections.get(key);
    if (section) ordered.push(section);
    sections.delete(key);
  }
  ordered.push(...sections.values());
  return ordered;
});

function isCover(photo: LocalPhoto) {
  return localPhotos.value[0]?.key === photo.key;
}

function normalizeOverrides(
  images?: PrestashopImageOverride[]
): PrestashopImageOverride[] {
  if (!images?.length) return [];
  return images.map((image) => ({
    type: image.type,
    id: image.id,
    file: image.file,
    url: image.url,
    rotation: image.rotation ?? 0,
    colorId: image.colorId ?? null,
    colorName: image.colorName ?? null,
  }));
}

function findSourceImage(imageId: string | number | undefined) {
  if (!imageId) return null;
  return (
    props.sourceImages.find((image) => String(image.id) === String(imageId)) ??
    null
  );
}

function buildInitialOverrides(): PrestashopImageOverride[] {
  const saved = normalizeOverrides(props.savedImages).filter((image) =>
    hasColorAssociation(image.colorId ?? null, image.colorName ?? null)
  );
  if (saved.length) {
    if (!collidingNames().length) return saved;
    return saved.map((image) => {
      const source = findSourceImage(image.id);
      const sourceName =
        source?.sourceColorName?.trim() || source?.colorName?.trim() || null;
      const placed = placeSourceColor(sourceName);
      if (!placed) return image;
      return { ...image, ...placed };
    });
  }
  return props.sourceImages
    .filter((image) =>
      Boolean(image.sourceColorName?.trim() || image.colorName?.trim()),
    )
    .map((image) => {
      const sourceName = image.sourceColorName?.trim() || image.colorName?.trim() || null;
      const placed = placeSourceColor(sourceName);
      if (placed) {
        return {
          type: "shopify" as const,
          id: image.id,
          url: image.url,
          rotation: 0,
          ...placed,
        };
      }
      const matched = matchSelectedColor(null, image.colorName ?? null);
      return {
        type: "shopify" as const,
        id: image.id,
        url: image.url,
        rotation: 0,
        colorId: matched?.id ?? null,
        colorName: image.colorName ?? matched?.nom ?? null,
      };
    });
}

function overridesEqual(
  left: PrestashopImageOverride[],
  right: PrestashopImageOverride[]
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function toOverrides(photos: LocalPhoto[]): PrestashopImageOverride[] {
  return photos.map((photo) => ({
    type: photo.type,
    id: photo.shopifyImageId,
    url: photo.shopifyUrl,
    file: photo.customFile,
    rotation: photo.rotation,
    colorId: photo.colorId,
    colorName: photo.colorName,
  }));
}

function loadPreview(override: PrestashopImageOverride): LocalPhoto | null {
  const rotation = override.rotation ?? 0;
  const source = findSourceImage(override.id);
  const colorName =
    override.colorName ?? source?.colorName ?? null;
  const matched = matchSelectedColor(override.colorId ?? null, colorName);
  const colorId = matched?.id ?? override.colorId ?? null;
  if (!hasColorAssociation(colorId, colorName ?? matched?.nom ?? null)) {
    return null;
  }
  if (override.type === "shopify" && override.id) {
    const previewUrl =
      override.url || source?.url || String(override.id);
    return {
      key: `shopify-${override.id}-${normalizeColorLabel(colorName)}`,
      type: "shopify",
      shopifyImageId: String(override.id),
      shopifyUrl: previewUrl,
      previewUrl,
      rotation,
      colorId,
      colorName: colorName ?? matched?.nom ?? null,
    };
  }
  if (override.type === "custom" && override.file) {
    return {
      key: `custom-${override.file}`,
      type: "custom",
      customFile: override.file,
      previewUrl: getShopifyCustomImageUrl(props.productGid, override.file),
      rotation,
      colorId,
      colorName: colorName ?? matched?.nom ?? null,
    };
  }
  return null;
}

async function loadPhotosFromOverrides(overrides: PrestashopImageOverride[]) {
  const token = ++loadToken;
  loading.value = true;
  localPhotos.value = [];
  try {
    const results = overrides
      .map((item) => loadPreview(item))
      .filter((item): item is LocalPhoto => Boolean(item));
    if (token !== loadToken) return;
    localPhotos.value = results;
    const placed = toOverrides(localPhotos.value);
    lastPersistedOverrides.value = placed;
    emit("update:images", placed);
  } finally {
    if (token === loadToken) loading.value = false;
  }
}

async function loadPhotos() {
  await loadPhotosFromOverrides(buildInitialOverrides());
}

async function persistPhotos() {
  const images = toOverrides(localPhotos.value);
  if (overridesEqual(images, lastPersistedOverrides.value)) {
    emit("update:images", images);
    return;
  }
  persisting.value = true;
  try {
    const response = await saveShopifyProductImages(props.productGid, images);
    const savedImages = normalizeOverrides(response.images);
    lastPersistedOverrides.value = savedImages;
    await loadPhotosFromOverrides(savedImages);
    emit("update:images", savedImages);
    emit("saved", savedImages);
  } catch (caught) {
    emit(
      "error",
      caught instanceof Error
        ? caught.message
        : "Sauvegarde des photos impossible."
    );
    throw caught;
  } finally {
    persisting.value = false;
  }
}

function resolveSectionMeta(sectionKey: string): {
  colorId: number | null;
  colorName: string | null;
} {
  const section = photoSections.value.find((item) => item.key === sectionKey);
  return {
    colorId: section?.colorId ?? null,
    colorName: section?.colorName ?? null,
  };
}

function triggerFileDialog(sectionKey: string) {
  pendingUploadSectionKey.value = sectionKey;
  fileInputRef.value?.click();
}

async function addFiles(files: FileList | File[], sectionKey: string) {
  const candidates = Array.from(files).filter((file) =>
    allowedTypes.has(file.type.toLowerCase())
  );
  if (!candidates.length) {
    emit("error", "Format non autorisé. Utilisez JPG, PNG, WEBP ou GIF.");
    return;
  }
  const remaining = maxPhotos - localPhotos.value.length;
  if (remaining <= 0) {
    emit("error", `Nombre maximum de photos atteint (${maxPhotos}).`);
    return;
  }
  const meta = resolveSectionMeta(sectionKey);
  if (!hasColorAssociation(meta.colorId, meta.colorName)) {
    emit(
      "error",
      "Chaque photo doit être associée à une couleur. Sélectionnez une couleur d’abord."
    );
    return;
  }
  uploading.value = true;
  uploadSectionKey.value = sectionKey;
  try {
    for (const file of candidates.slice(0, remaining)) {
      const uploaded = await uploadShopifyCustomImage(props.productGid, file);
      localPhotos.value.push({
        key: `custom-${uploaded.file}`,
        type: "custom",
        customFile: uploaded.file,
        previewUrl: getShopifyCustomImageUrl(props.productGid, uploaded.file),
        rotation: 0,
        colorId: meta.colorId,
        colorName: meta.colorName,
      });
    }
    await persistPhotos();
  } catch (caught) {
    if (!(caught instanceof Error)) {
      emit("error", "Ajout de photo impossible.");
    }
  } finally {
    uploading.value = false;
    uploadSectionKey.value = null;
    pendingUploadSectionKey.value = null;
  }
}

function handleFileInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const sectionKey = pendingUploadSectionKey.value;
  if (!sectionKey) {
    emit(
      "error",
      "Chaque photo doit être associée à une couleur. Choisissez une section couleur."
    );
    input.value = "";
    return;
  }
  if (input.files?.length) void addFiles(input.files, sectionKey);
  input.value = "";
}

function handleUploadDrop(event: DragEvent, sectionKey: string) {
  isDragOverUpload.value = false;
  uploadSectionKey.value = null;
  if (event.dataTransfer?.files?.length) {
    void addFiles(event.dataTransfer.files, sectionKey);
  }
}

function findPhotoIndex(key: string) {
  return localPhotos.value.findIndex((photo) => photo.key === key);
}

async function rotatePhoto(key: string) {
  const index = findPhotoIndex(key);
  const photo = localPhotos.value[index];
  if (!photo) return;
  photo.rotation = (photo.rotation + 90) % 360;
  try {
    await persistPhotos();
  } catch {
    photo.rotation = (photo.rotation + 270) % 360;
  }
}

async function setCover(key: string) {
  const index = findPhotoIndex(key);
  if (index <= 0) return;
  const photos = [...localPhotos.value];
  const [selected] = photos.splice(index, 1);
  photos.unshift(selected);
  localPhotos.value = photos;
  try {
    await persistPhotos();
  } catch {
    await loadPhotosFromOverrides(lastPersistedOverrides.value);
  }
}

function requestDelete(key: string) {
  if (localPhotos.value.length <= 1) {
    emit(
      "error",
      "Impossible de supprimer la dernière photo : au moins une image est requise pour la mise en ligne."
    );
    return;
  }
  deleteKey.value = key;
  showDeleteModal.value = true;
}

function cancelDelete() {
  showDeleteModal.value = false;
  deleteKey.value = null;
}

async function confirmDelete() {
  const key = deleteKey.value;
  showDeleteModal.value = false;
  deleteKey.value = null;
  if (!key) return;
  const index = findPhotoIndex(key);
  if (index < 0) return;
  localPhotos.value.splice(index, 1);
  try {
    await persistPhotos();
  } catch {
    await loadPhotosFromOverrides(lastPersistedOverrides.value);
  }
}

function handleDragStart(event: DragEvent, key: string) {
  draggingKey.value = key;
  event.dataTransfer?.setData("text/plain", key);
  if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
}

function handleDragEnd() {
  draggingKey.value = null;
  dragOverKey.value = null;
}

function handleDragOver(event: DragEvent, key: string) {
  event.preventDefault();
  dragOverKey.value = key;
}

async function handleDrop(
  event: DragEvent,
  sectionKey: string,
  targetKey: string
) {
  event.preventDefault();
  const sourceKey = draggingKey.value;
  draggingKey.value = null;
  dragOverKey.value = null;
  if (!sourceKey || sourceKey === targetKey) return;
  const sourceIndex = findPhotoIndex(sourceKey);
  const targetIndex = findPhotoIndex(targetKey);
  if (sourceIndex < 0 || targetIndex < 0) return;
  const photos = [...localPhotos.value];
  const [moved] = photos.splice(sourceIndex, 1);
  const meta = resolveSectionMeta(sectionKey);
  if (!hasColorAssociation(meta.colorId, meta.colorName)) {
    emit(
      "error",
      "Impossible de déplacer une photo vers une section sans couleur."
    );
    return;
  }
  moved.colorId = meta.colorId;
  moved.colorName = meta.colorName;
  const insertAt = findPhotoIndex(targetKey);
  const safeIndex = insertAt < 0 ? photos.length : insertAt;
  photos.splice(safeIndex, 0, moved);
  localPhotos.value = photos;
  try {
    await persistPhotos();
  } catch {
    await loadPhotosFromOverrides(lastPersistedOverrides.value);
  }
}

watch(
  () =>
    [
      props.productGid,
      props.sourceImages
        .map((image) => `${image.id}:${image.colorName ?? ""}`)
        .join(","),
      (props.selectedColors ?? []).map((color) => color.id).join(","),
      (props.colorSourceChoices ?? [])
        .map((choice) => `${choice.sourceName}:${choice.colorId}`)
        .join(","),
    ] as const,
  () => {
    void loadPhotos();
  },
  { immediate: true }
);

onUnmounted(() => {
  loadToken += 1;
});

defineExpose({
  photoCount: () => localPhotos.value.length,
});
</script>
